import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SportsIcon from '@mui/icons-material/Sports';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink } from 'react-router-dom';
import api from '@/services/api';
import PageHeader from '@/components/Layout/PageHeader';
import { ReviewSessionCard } from '@/components/review/ReviewSessionListItems';
import { useAuth } from '@/hooks/useAuth';
import type { LiveSession, ReviewSession } from '@/types';

interface DashboardData {
  stats: { review_count: number; live_count: number };
  recent: { review: ReviewSession[]; live: LiveSession[] };
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [reviewRecent, setReviewRecent] = useState<ReviewSession[]>([]);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<DashboardData>('/dashboard'),
      api.get<{ sessions: ReviewSession[]; total: number }>('/review-sessions', {
        params: { limit: 5, filter: 'all' },
      }),
    ])
      .then(([dashboardRes, reviewRes]) => {
        setData(dashboardRes.data);
        setReviewRecent(reviewRes.data.sessions);
        setReviewCount(reviewRes.data.total);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Typography>載入中…</Typography>;
  if (!data) return <Typography color="error">無法載入總覽</Typography>;

  return (
    <Box>
      <PageHeader
        icon={<DashboardIcon />}
        title="總覽"
        subtitle={`${user?.full_name || user?.reference_no || '我的'} 的影片分析與即時記錄`}
        chips={
          <>
            <Chip size="small" color="secondary" variant="outlined" label={`${reviewCount} 場分析`} />
            <Chip size="small" variant="outlined" label={`${data.stats.live_count} 場即時`} />
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardActionArea component={RouterLink} to="/review">
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <PlayCircleIcon color="secondary" sx={{ fontSize: 48 }} />
                  <Box>
                    <Typography variant="h5">{reviewCount}</Typography>
                    <Typography color="text.secondary">影片分析場次</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardActionArea component={RouterLink} to="/live">
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <SportsIcon color="secondary" sx={{ fontSize: 48 }} />
                  <Box>
                    <Typography variant="h5">{data.stats.live_count}</Typography>
                    <Typography color="text.secondary">即時記錄場次</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button component={RouterLink} to="/review?new=1" variant="contained" color="secondary" startIcon={<AddIcon />}>
          新增影片分析
        </Button>
        <Button component={RouterLink} to="/live?new=1" variant="outlined" color="secondary" startIcon={<AddIcon />}>
          新增即時記錄
        </Button>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="h6">最近影片分析</Typography>
            <Button component={RouterLink} to="/review" size="small" color="secondary">
              查看全部
            </Button>
          </Stack>
          {reviewRecent.length === 0 ? (
            <Typography color="text.secondary">尚無記錄</Typography>
          ) : (
            <Stack spacing={1.25}>
              {reviewRecent.map((s) => (
                <ReviewSessionCard key={s.id} session={s} compact />
              ))}
            </Stack>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="h6">最近即時記錄</Typography>
            <Button component={RouterLink} to="/live" size="small" color="secondary">
              查看全部
            </Button>
          </Stack>
          {data.recent.live.length === 0 ? (
            <Typography color="text.secondary">尚無記錄</Typography>
          ) : (
            data.recent.live.map((s) => (
              <Card key={s.id} sx={{ mb: 1.25 }} variant="outlined">
                <CardActionArea component={RouterLink} to={`/live/${s.id}`}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={700} noWrap>
                          {s.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {[s.home_team, s.away_team].filter(Boolean).join(' vs ') || '—'}
                        </Typography>
                      </Box>
                      <Chip size="small" label={s.status === 'ended' ? '已結束' : '進行中'} />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
